import { appInjector } from '../app-injector';
import { ConfirmationComponent } from '../modals/Confirmation.component';
import { ModalsService } from '../modals/Modals.service';
import { SingleChoiceConfirmationComponent } from '../modals/SingleChoiceConfirmation.component';
import { TranslationService } from '../translations';
/*
    Note that passing lambdas in does not work well because of how decorators are
    implemented. For this reason use the construct "function() {...}" in order
    to preserve the right object context.
*/
export class ConfirmationHelpers {
    /**
     * Generates a confirmation modal that resolves when the client hits "Yes"
     * to the prompted question.
     *
     * @example
     * ConfirmationHelpers
     *     .generateConfirmationModal('Hey is your computer on fire???')
     *     .then(() => {
     *          console.log('Nice! Looks like their computer is one fire');
     *     });
     */
    public static generateConfirmationModal(
        text: string,
        subtitleText: string = null,
        confirmationButtonKeys: {
            yes: string;
            no: string;
        } = {
            yes: 'text.common-terms.yes',
            no: 'text.common-terms.no'
        }
    ) {
        return new Promise((resolve, reject) => {
            const modalService = appInjector().get(ModalsService);
            modalService.openModal('confirmation', {
                className: 'confirmationModal',
                TRtitle: null,
                body: {
                    type: ConfirmationComponent,
                    properties: {
                        text,
                        subtitleText,
                        confirmationButtonKeys,
                        onlyAcknowledge: false,
                        leftAlignedText: false
                    },
                    events: {
                        confirm: (confirmation: boolean) => {
                            if (confirmation) {
                                resolve(null);
                            } else {
                                reject();
                            }
                            modalService.closeModal('confirmation');
                        }
                    }
                }
            });
        });
    }

    public static requireConfirmationDecorator(
        textKey: string,
        subtitleTextKey: string = null
    ): MethodDecorator {
        return function (
            _target,
            _propertyKey: string | symbol,
            descriptor: PropertyDescriptor
        ) {
            const fn = descriptor.value;
            descriptor.value = function (...args: any[]) {
                const TR = appInjector().get(TranslationService);
                const subtitleText = subtitleTextKey
                    ? TR.key(subtitleTextKey)
                    : null;
                // Return a promise so we can have a confirmation chained with
                // a notification decorator on methods that hit apis
                return ConfirmationHelpers.generateConfirmationModal(
                    TR.key(textKey),
                    subtitleText
                ).then(() => fn.apply(this, args));
            };
        };
    }

    public static generateSingleChoiceConfirmationModal(
        text: string,
        confirmationButtonKeys: {
            yes: string;
        } = {
            yes: 'text.common-terms.yes'
        }
    ) {
        return new Promise((resolve) => {
            const modalService = appInjector().get(ModalsService);
            modalService.openModal('confirmation', {
                className: 'confirmationModal',
                TRtitle: null,
                body: {
                    type: SingleChoiceConfirmationComponent,
                    properties: {
                        text,
                        confirmationButtonKeys
                    },
                    events: {
                        confirm: (confirmation: boolean) => {
                            if (confirmation) {
                                resolve(null);
                            }
                            modalService.closeModal('confirmation');
                        }
                    }
                }
            });
        });
    }
}
